import LogRocket from 'logrocket';
import ReactGA from 'react-ga4';

// Analytics service configuration
interface AnalyticsConfig {
  logRocketAppId: string;
  googleAnalyticsId: string;
}

// User identification data
interface UserData {
  id: string | number;
  name?: string;
  email?: string;
  [key: string]: any; // Allow additional user properties
}

class AnalyticsService {
  private static instance: AnalyticsService;
  private initialized = false;
  private config: AnalyticsConfig;

  private constructor() {
    this.config = {
      logRocketAppId: 'rxjm1g/danel-feinkost',
      googleAnalyticsId: 'G-CKQ91TVCTC',
    };
  }

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  public initialize(): void {
    if (this.initialized || !import.meta.env.PROD || window.location.host.includes('local')) return;

    // Initialize LogRocket
    LogRocket.init(this.config.logRocketAppId);

    // Initialize Google Analytics
    ReactGA.initialize(this.config.googleAnalyticsId);

    this.initialized = true;
  }

  public identifyUser(userData: UserData): void {
    if (!import.meta.env.PROD) return;

    // Create a clean user object without undefined values
    const userProperties = Object.entries(userData).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, string | number | boolean>);

    // Identify user in LogRocket
    LogRocket.identify(String(userData.id), userProperties);

    // Identify user in Google Analytics
    ReactGA.set({
      userId: String(userData.id),
      user_properties: userProperties,
    });
  }

  public trackEvent(category: string, action: string, label?: string, value?: number): void {
    if (!import.meta.env.PROD) return;

    // Track event in Google Analytics
    ReactGA.event({
      category,
      action,
      label,
      value,
    });
  }

  public trackPageView(path: string): void {
    if (!import.meta.env.PROD) return;

    // Track page view in Google Analytics
    ReactGA.send({ hitType: "pageview", page: path });
  }
}

// Export a singleton instance
export const analytics = AnalyticsService.getInstance(); 